package one.healthium.insights.ui.components.items

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.daysBetween
import one.healthium.insights.domain.models.ContentEntry
import one.healthium.insights.domain.models.TipModel
import one.healthium.insights.domain.models.TipsConfigModel
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.utils.toColor
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.koinInject
import kotlin.math.max
import kotlin.time.Clock
import kotlin.time.Instant

@Composable
internal fun Tips(
    modifier: Modifier = Modifier,
    item: ContentEntry.Tips,
) {
    val tip = rememberCurrentTip(item)
    if (tip != null) {
        Tip(modifier = modifier, tip = tip, config = item.config)
    }
}

@Composable
private fun Tip(
    modifier: Modifier = Modifier,
    tip: TipModel,
    config: TipsConfigModel? = null,
) {
    Column(
        modifier = modifier
            .padding(top = 16.dp, bottom = 6.dp, start = 12.dp, end = 12.dp)
            .background(
                color = config?.backgroundColor?.toColor() ?: AppTheme.colors.primary,
                shape = AppTheme.shapes.small,
            )
            .padding(24.dp)
            .testTag("insights_tip")
    ) {
        Text(
            text = tip.title,
            style = AppTheme.textStyles.poppins17Bold,
            color = config?.textColor?.toColor() ?: AppTheme.colors.onPrimary,
        )
        Spacer(Modifier.height(8.dp))
        Text(
            modifier = Modifier.testTag("tip_text"),
            text = tip.text,
            style = AppTheme.textStyles.secondary_17_500,
            color = config?.textColor?.toColor() ?: AppTheme.colors.onPrimary,
        )
    }
}


@Composable
private fun rememberCurrentTip(item: ContentEntry.Tips): TipModel? {
    val appPreferences = koinInject<AppPreferences>()
    return remember(item) {
        if (item.tips.isEmpty()) return@remember null

        val now = Clock.System.now()
        val latestShownTipTimestamp = appPreferences.latestShownTipTimestamp
        val latestShownTipId = appPreferences.latestShownTipId

        val tip = if (latestShownTipTimestamp != null) {
            val indexOfLatestShownTip = if (latestShownTipId != null) {
                item.tips.indexOfFirst { it.id == latestShownTipId }
            } else {
                -1
            }

            val daysBetweenNow = Instant.fromEpochMilliseconds(latestShownTipTimestamp).daysBetween(now)
            if (daysBetweenNow > 0) {
                appPreferences.latestShownTipTimestamp = now.toEpochMilliseconds()

                var nextTipIndex = indexOfLatestShownTip + 1
                if (nextTipIndex >= item.tips.size) nextTipIndex = 0

                item.tips[nextTipIndex]
            } else {
                item.tips[max(0, indexOfLatestShownTip)]
            }
        } else {
            appPreferences.latestShownTipTimestamp = now.toEpochMilliseconds()
            item.tips.first()
        }
        appPreferences.latestShownTipId = tip.id
        tip
    }
}

@Composable
@Preview
private fun TipPreview() {
    Column {
        AppTheme(Themes.carnivore()) {
            Tip(
                tip = TipModel(
                    id = "1",
                    title = "Tip Title",
                    text = "This is a tip text that provides useful information.",
                )
            )
        }
        AppTheme(Themes.pcos()) {
            Tip(
                tip = TipModel(
                    id = "1",
                    title = "Tip Title",
                    text = "This is a tip text that provides useful information.",
                )
            )
        }
        AppTheme(Themes.med()) {
            Tip(
                tip = TipModel(
                    id = "1",
                    title = "Tip Title",
                    text = "This is a tip text that provides useful information.",
                )
            )
        }
    }
}